import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, CheckCircle2, Loader2 } from 'lucide-react';

/**
 * Hero panel. Everything displayed here is measured, not authored:
 * the detector weights are really fetched and initialised in the visitor's
 * browser and the timing shown is the real elapsed load time.
 *
 * Only the 193KB detector loads here — the 6.4MB recognition net is deferred
 * to /sandbox so the landing page stays light. face-api itself is imported
 * dynamically so it never lands in the initial landing-page chunk.
 */

const PIPELINE = [
  { label: 'Document capture & portrait extraction', where: 'on-device' },
  { label: 'Blink / head-pose liveness', where: 'on-device' },
  { label: 'Face match against ID portrait', where: 'on-device' },
  { label: 'PDF417 barcode & document authentication', where: 'server' },
  { label: 'Tamper-evident audit record', where: 'server' },
] as const;

export default function HeroLiveDemo() {
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Deferred to idle time: proving the detector works must never delay paint.
    const kick = async () => {
      const t0 = performance.now();
      try {
        const faceapi = await import('@vladmandic/face-api');
        await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
        if (cancelled) return;
        setLoadMs(Math.round(performance.now() - t0));
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    const id = window.setTimeout(kick, 400);
    return () => { cancelled = true; window.clearTimeout(id); };
  }, []);

  return (
    <div className="relative w-full max-w-3xl mx-auto mt-12">
      <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-teal-500/20 via-transparent to-cyan-500/10 blur-xl pointer-events-none" />

      <div className="relative rounded-2xl border border-teal-700/30 bg-slate-900/80 backdrop-blur-sm overflow-hidden shadow-2xl">
        {/* Top bar — honest status, no fake "LIVE" light */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900/50">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
          <span className="ml-3 text-xs text-slate-500 font-mono">agos — verification pipeline</span>
          <div className="ml-auto flex items-center gap-1.5 text-[10px] font-mono">
            {failed ? (
              <span className="text-slate-500">detector offline</span>
            ) : loadMs === null ? (
              <>
                <Loader2 className="w-3 h-3 text-teal-400 animate-spin" />
                <span className="text-slate-400">loading detector…</span>
              </>
            ) : (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-emerald-400">detector ready · {loadMs}ms</span>
              </>
            )}
          </div>
        </div>

        <div className="p-5 md:p-6">
          {/* Pipeline — with an honest "runs where" column */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
              What runs where
            </p>
            <p className="text-[10px] text-slate-600 font-mono">
              {typeof navigator !== 'undefined' && navigator.hardwareConcurrency
                ? `${navigator.hardwareConcurrency} cores detected`
                : ''}
            </p>
          </div>

          <div className="space-y-1.5 mb-6">
            {PIPELINE.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.35 }}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 border border-slate-800 bg-slate-800/30"
              >
                <CheckCircle2
                  className={`w-4 h-4 shrink-0 ${
                    step.where === 'on-device' ? 'text-teal-400' : 'text-slate-500'
                  }`}
                />
                <span className="text-xs text-slate-300 flex-1 min-w-0 truncate">{step.label}</span>
                <span
                  className={`text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded shrink-0 ${
                    step.where === 'on-device'
                      ? 'text-teal-300 bg-teal-500/10 border border-teal-700/30'
                      : 'text-slate-400 bg-slate-700/40 border border-slate-700'
                  }`}
                >
                  {step.where}
                </span>
              </motion.div>
            ))}
          </div>

          {/* The actual proof-of-product CTA */}
          <div className="rounded-xl border border-teal-700/30 bg-teal-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-white mb-1">
                Don't take our word for it — run one.
              </p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Real inference in your browser. No signup, no upload, no sales call.
              </p>
            </div>
            <Link
              to="/sandbox"
              className="group inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-teal-600 to-teal-500 text-white rounded-xl font-semibold text-sm shrink-0 shadow-lg shadow-teal-900/30 transition-all hover:shadow-teal-800/40"
            >
              <Cpu className="w-4 h-4" />
              Open the sandbox
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
